import Phaser from "phaser";
import { PuzzleState } from "./PuzzleModel";

type TileObj = {
  id: number;
  container: Phaser.GameObjects.Container;
  border: Phaser.GameObjects.Graphics;
};

type LastPiece = {
  container: Phaser.GameObjects.Container;
};

export class PuzzleView {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container;
  private pictureKey: string;
  private tiles = new Map<number, TileObj>();
  private indexToPos = new Map<number, Phaser.Math.Vector2>();
  private boardSizePx: number;
  private gapPx: number;
  private tileSizePx = 0;
  private n = 0;
  private onTilePressed: (tileId: number) => void;
  private blank?: Phaser.GameObjects.Container;
  private lastPiece?: LastPiece;
  private currentState?: PuzzleState;
  private inputEnabled = true;
  private previewImage?: Phaser.GameObjects.Image;
  private previewVisible = false;

  constructor(args: {
    scene: Phaser.Scene;
    x: number;
    y: number;
    pictureKey: string;
    boardSizePx: number;
    gapPx: number;
    onTilePressed: (tileId: number) => void;
  }) {
    this.scene = args.scene;
    this.pictureKey = args.pictureKey;
    this.boardSizePx = args.boardSizePx;
    this.gapPx = args.gapPx;
    this.onTilePressed = args.onTilePressed;
    this.container = this.scene.add.container(args.x, args.y);
    this.container.setDepth(10);

    // Scene全体の入力でクリック位置から判定（タイミング問題を回避）
    this.scene.input.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      if (!this.inputEnabled) return;
      const id = this.pickTileId(pointer);
      if (id == null) return;
      if (!this.isAdjacent(id)) return;
      this.onTilePressed(id);
    });
  }

  destroy() {
    this.scene.input.off("pointerdown");
    if (this.previewImage) {
      this.previewImage.destroy();
      this.previewImage = undefined;
    }
    this.container.destroy(true);
    this.tiles.clear();
    this.indexToPos.clear();
  }

  build(state: PuzzleState) {
    this.container.removeAll(true);
    this.tiles.clear();
    this.indexToPos.clear();
    this.blank = undefined;
    this.lastPiece = undefined;

    this.n = state.n;
    const n = state.n;
    const tileSize = (this.boardSizePx - this.gapPx * (n - 1)) / n;
    this.tileSizePx = tileSize;

    const tex = this.scene.textures.get(this.pictureKey);
    const src = tex.getSourceImage() as HTMLImageElement | HTMLCanvasElement;
    const srcW = (src as any).width;
    const srcH = (src as any).height;
    const sliceW = Math.floor(srcW / n);
    const sliceH = Math.floor(srcH / n);

    // index→座標のマッピング
    const originX = -this.boardSizePx / 2 + tileSize / 2;
    const originY = -this.boardSizePx / 2 + tileSize / 2;
    for (let i = 0; i < n * n; i++) {
      const x = i % n;
      const y = Math.floor(i / n);
      const px = originX + x * (tileSize + this.gapPx);
      const py = originY + y * (tileSize + this.gapPx);
      this.indexToPos.set(i, new Phaser.Math.Vector2(px, py));
    }

    // 空白マス
    const blankG = this.scene.add.graphics();
    blankG.fillStyle(0x000000, 0.35);
    blankG.fillRoundedRect(-tileSize / 2, -tileSize / 2, tileSize, tileSize, 12);
    blankG.lineStyle(4, 0xffffff, 0.18);
    blankG.strokeRoundedRect(-tileSize / 2, -tileSize / 2, tileSize, tileSize, 12);
    const blankContainer = this.scene.add.container(0, 0, [blankG]);
    blankContainer.setSize(tileSize, tileSize);
    blankContainer.setDepth(0);
    this.container.add(blankContainer);
    this.blank = blankContainer;

    // タイル生成（1〜n*n-1）
    for (let tileId = 1; tileId <= n * n - 1; tileId++) {
      const solvedIndex = tileId - 1;
      const solvedX = solvedIndex % n;
      const solvedY = Math.floor(solvedIndex / n);
      const frameKey = `${this.pictureKey}_t${n}_${tileId}`;

      if (!this.scene.textures.exists(frameKey)) {
        const sx = solvedX * sliceW;
        const sy = solvedY * sliceH;
        const canvasTex = this.scene.textures.createCanvas(frameKey, sliceW, sliceH);
        if (canvasTex) {
          const ctx = canvasTex.getContext();
          if (ctx) {
            ctx.drawImage(src as any, sx, sy, sliceW, sliceH, 0, 0, sliceW, sliceH);
            canvasTex.refresh();
          }
        }
      }

      const img = this.scene.add.image(0, 0, frameKey);
      img.setDisplaySize(tileSize, tileSize);

      const g = this.scene.add.graphics();
      g.lineStyle(4, 0xffffff, 0.25);
      g.strokeRect(-tileSize / 2, -tileSize / 2, tileSize, tileSize);

      const tileContainer = this.scene.add.container(0, 0, [img, g]);
      tileContainer.setSize(tileSize, tileSize);
      tileContainer.setDepth(10);

      this.container.add(tileContainer);
      this.tiles.set(tileId, { id: tileId, container: tileContainer, border: g });
    }

    // 最後のピース（右下、n*n番目）を作成して非表示に
    {
      const lastTileId = n * n;
      const solvedIndex = lastTileId - 1; // 右下 = n*n-1
      const solvedX = solvedIndex % n;
      const solvedY = Math.floor(solvedIndex / n);
      const frameKey = `${this.pictureKey}_t${n}_${lastTileId}`;

      if (!this.scene.textures.exists(frameKey)) {
        const sx = solvedX * sliceW;
        const sy = solvedY * sliceH;
        const canvasTex = this.scene.textures.createCanvas(frameKey, sliceW, sliceH);
        if (canvasTex) {
          const ctx = canvasTex.getContext();
          if (ctx) {
            ctx.drawImage(src as any, sx, sy, sliceW, sliceH, 0, 0, sliceW, sliceH);
            canvasTex.refresh();
          }
        }
      }

      const img = this.scene.add.image(0, 0, frameKey);
      img.setDisplaySize(tileSize, tileSize);

      const lastPos = this.indexToPos.get(solvedIndex);
      const lc = this.scene.add.container(lastPos?.x ?? 0, lastPos?.y ?? 0, [img]);
      lc.setSize(tileSize, tileSize);
      lc.setDepth(10);
      lc.setVisible(false); // 最初は非表示
      lc.setAlpha(0);
      this.container.add(lc);
      this.lastPiece = { container: lc };
    }

    this.syncPositions(state);
    this.currentState = { n: state.n, board: [...state.board], blankIndex: state.blankIndex };
    this.updateHighlight();
  }

  /** 即座にすべてのピースを正しい位置に配置 */
  syncPositions(state: PuzzleState) {
    this.currentState = { n: state.n, board: [...state.board], blankIndex: state.blankIndex };
    
    for (let index = 0; index < state.n * state.n; index++) {
      const id = state.board[index];
      if (id === 0) continue;
      const obj = this.tiles.get(id);
      const pos = this.indexToPos.get(index);
      if (obj && pos) {
        obj.container.x = pos.x;
        obj.container.y = pos.y;
      }
    }
    
    const blankPos = this.indexToPos.get(state.blankIndex);
    if (this.blank && blankPos) {
      this.blank.x = blankPos.x;
      this.blank.y = blankPos.y;
    }
    
    this.updateHighlight();
  }

  /** 1枚のタイルをアニメーションで移動 */
  animateTile(tileId: number, toIndex: number, newState: PuzzleState, onComplete?: () => void) {
    const obj = this.tiles.get(tileId);
    const toPos = this.indexToPos.get(toIndex);
    const blankToPos = this.indexToPos.get(newState.blankIndex);

    // 先に状態を更新（これが最重要）
    this.currentState = { n: newState.n, board: [...newState.board], blankIndex: newState.blankIndex };

    if (!obj || !toPos || !blankToPos || !this.blank) {
      this.syncPositions(newState);
      this.updateHighlight();
      onComplete?.();
      return;
    }

    const duration = 120;
    let pending = 2;
    const done = () => {
      pending--;
      if (pending <= 0) {
        // アニメ後に位置をスナップ
        obj.container.x = toPos.x;
        obj.container.y = toPos.y;
        this.blank!.x = blankToPos.x;
        this.blank!.y = blankToPos.y;
        this.updateHighlight();
        onComplete?.();
      }
    };

    // タイルを移動
    this.scene.tweens.killTweensOf(obj.container);
    this.scene.tweens.add({
      targets: obj.container,
      x: toPos.x,
      y: toPos.y,
      duration,
      ease: "Quad.easeOut",
      onComplete: done,
    });

    // 空白を移動
    this.scene.tweens.killTweensOf(this.blank);
    this.scene.tweens.add({
      targets: this.blank,
      x: blankToPos.x,
      y: blankToPos.y,
      duration,
      ease: "Quad.easeOut",
      onComplete: done,
    });
  }

  setInputEnabled(v: boolean) {
    this.inputEnabled = v;
  }

  /** 完成時に最後のピースを表示して空白を隠す */
  showLastPiece(onComplete?: () => void) {
    if (!this.lastPiece || !this.blank) {
      onComplete?.();
      return;
    }

    // 空白を非表示に
    this.scene.tweens.add({
      targets: this.blank,
      alpha: 0,
      duration: 200,
      ease: "Quad.easeOut",
    });

    // 最後のピースをフェードイン
    this.lastPiece.container.setVisible(true);
    this.scene.tweens.add({
      targets: this.lastPiece.container,
      alpha: 1,
      duration: 400,
      ease: "Quad.easeOut",
      onComplete: () => {
        onComplete?.();
      },
    });
  }

  /** 完成後に元の1枚絵を表示してタイルをフェードアウト */
  mergeToFullImage(onComplete?: () => void) {
    const duration = 500;

    // 元の1枚画像を盤面サイズで表示
    const fullImage = this.scene.add.image(0, 0, this.pictureKey);
    fullImage.setDisplaySize(this.boardSizePx, this.boardSizePx);
    fullImage.setDepth(5); // タイルより下に配置
    fullImage.setAlpha(0);
    this.container.add(fullImage);

    // 1枚絵をフェードイン
    this.scene.tweens.add({
      targets: fullImage,
      alpha: 1,
      duration: duration,
      ease: "Quad.easeOut",
    });

    // 全タイル（枠線含む）をフェードアウト
    const targets: Phaser.GameObjects.Container[] = [];
    for (const t of this.tiles.values()) {
      targets.push(t.container);
    }
    if (this.lastPiece) {
      targets.push(this.lastPiece.container);
    }

    if (targets.length > 0) {
      this.scene.tweens.add({
        targets,
        alpha: 0,
        duration: duration,
        ease: "Quad.easeOut",
        onComplete: () => {
          onComplete?.();
        },
      });
    } else {
      this.scene.time.delayedCall(duration, () => onComplete?.());
    }
  }

  /** プレビュー画像の表示/非表示を切り替え */
  togglePreview() {
    if (this.previewVisible) {
      this.hidePreview();
    } else {
      this.showPreview();
    }
  }

  /** プレビュー画像を表示 */
  private showPreview() {
    if (this.previewVisible || !this.pictureKey) return;
    
    // プレビュー画像を作成（まだ存在しない場合）
    if (!this.previewImage) {
      this.previewImage = this.scene.add.image(0, 0, this.pictureKey);
      this.previewImage.setDisplaySize(this.boardSizePx, this.boardSizePx);
      this.previewImage.setAlpha(0.5); // 半透明
      this.previewImage.setDepth(20); // タイルより上に表示
      this.container.add(this.previewImage);
    }
    
    this.previewImage.setVisible(true);
    this.previewVisible = true;
  }

  /** プレビュー画像を非表示 */
  private hidePreview() {
    if (!this.previewVisible || !this.previewImage) return;
    this.previewImage.setVisible(false);
    this.previewVisible = false;
  }

  private updateHighlight() {
    if (!this.currentState) return;
    
    for (const t of this.tiles.values()) {
      t.border.clear();
      t.border.lineStyle(4, 0xffffff, 0.25);
      t.border.strokeRect(-this.tileSizePx / 2, -this.tileSizePx / 2, this.tileSizePx, this.tileSizePx);
    }
  }

  private getAdjacentTileIds(): Set<number> {
    const set = new Set<number>();
    if (!this.currentState) return set;
    
    const { n, board, blankIndex } = this.currentState;
    const bx = blankIndex % n;
    const by = Math.floor(blankIndex / n);
    
    const check = (idx: number) => {
      const id = board[idx];
      if (id !== 0) set.add(id);
    };
    
    if (bx > 0) check(blankIndex - 1);
    if (bx < n - 1) check(blankIndex + 1);
    if (by > 0) check(blankIndex - n);
    if (by < n - 1) check(blankIndex + n);
    
    return set;
  }

  private isAdjacent(tileId: number): boolean {
    return this.getAdjacentTileIds().has(tileId);
  }

  private pickTileId(pointer: Phaser.Input.Pointer): number | null {
    if (!this.currentState || this.tileSizePx <= 0) return null;

    // UI領域（右上200px四方）はクリックを無視
    const sw = this.scene.scale.width;
    if (pointer.x > sw - 200 && pointer.y < 200) return null;

    // ワールド座標→コンテナローカル座標
    const cam = this.scene.cameras.main;
    const wp = cam.getWorldPoint(pointer.x, pointer.y);
    const localX = wp.x - this.container.x;
    const localY = wp.y - this.container.y;

    const half = this.boardSizePx / 2;
    const x0 = localX + half;
    const y0 = localY + half;
    
    if (x0 < 0 || y0 < 0 || x0 >= this.boardSizePx || y0 >= this.boardSizePx) return null;

    const pitch = this.tileSizePx + this.gapPx;
    const gx = Math.floor(x0 / pitch);
    const gy = Math.floor(y0 / pitch);
    
    if (gx < 0 || gy < 0 || gx >= this.n || gy >= this.n) return null;

    const index = gy * this.n + gx;
    const id = this.currentState.board[index];
    return id === 0 ? null : id;
  }
}
